/** Parse VEVENT blocks from an ICS calendar payload into timed events. */
export type ParsedCalendarEvent = {
  uid: string;
  title: string;
  startsAt: Date;
  endsAt: Date;
};

function unfoldIcs(raw: string) {
  return raw.replace(/\r\n/g, "\n").replace(/\n[ \t]/g, "");
}

function unescapeIcsText(value: string) {
  return value
    .replace(/\\n/gi, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\");
}

function parseIcsDate(value: string, _params: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d{8}$/.test(trimmed)) {
    const year = Number(trimmed.slice(0, 4));
    const month = Number(trimmed.slice(4, 6)) - 1;
    const day = Number(trimmed.slice(6, 8));
    return new Date(year, month, day, 9, 0, 0, 0);
  }

  const match = trimmed.match(
    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z)?$/,
  );
  if (!match) return null;

  const [, y, mo, d, h, mi, s, z] = match;
  if (z) {
    return new Date(
      Date.UTC(
        Number(y),
        Number(mo) - 1,
        Number(d),
        Number(h),
        Number(mi),
        Number(s),
      ),
    );
  }
  return new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s),
  );
}

export function parseIcsEvents(raw: string): ParsedCalendarEvent[] {
  const text = unfoldIcs(raw);
  const blocks = text.split("BEGIN:VEVENT").slice(1);
  const events: ParsedCalendarEvent[] = [];

  for (const block of blocks) {
    const body = block.split("END:VEVENT")[0] ?? "";
    const lines = body
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    let uid = "";
    let title = "Calendar event";
    let startsAt: Date | null = null;
    let endsAt: Date | null = null;

    for (const line of lines) {
      const separator = line.indexOf(":");
      if (separator < 0) continue;
      const left = line.slice(0, separator);
      const value = line.slice(separator + 1);
      const [name, ...paramParts] = left.split(";");
      const params = paramParts.join(";");
      const key = (name ?? "").toUpperCase();

      if (key === "UID") uid = value.trim();
      if (key === "SUMMARY") title = unescapeIcsText(value).trim() || title;
      if (key === "DTSTART") startsAt = parseIcsDate(value, params);
      if (key === "DTEND") endsAt = parseIcsDate(value, params);
    }

    if (!startsAt) continue;
    if (!endsAt || endsAt <= startsAt) {
      endsAt = new Date(startsAt.getTime() + 60 * 60 * 1000);
    }

    events.push({
      uid: uid || `${startsAt.toISOString()}-${title}`,
      title,
      startsAt,
      endsAt,
    });
  }

  return events.sort(
    (left, right) => left.startsAt.getTime() - right.startsAt.getTime(),
  );
}
